import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  probeCamera, createCamera,
  type OnvifCreds, type OnvifProfile, type ProbeResult,
} from '../../api/cameras';

interface Props {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  onCreated: () => void;
}

/**
 * Hardware-agnostic camera onboarding. Step 1 probes the NVR/camera over
 * ONVIF to fetch device info + available profiles. Step 2 lets the user
 * name the camera, pick a profile, and choose how the stream reaches the
 * apps (URL pull / WHIP push).
 */
export const AddCameraModal: React.FC<Props> = ({ open, onClose, propertyId, onCreated }) => {
  const [step, setStep] = useState<'probe' | 'configure'>('probe');
  const [probing, setProbing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [creds, setCreds] = useState<OnvifCreds>({ host: '', username: 'admin', password: '', port: '80' });
  const [probe, setProbe] = useState<ProbeResult | null>(null);

  const [name, setName] = useState('');
  const [profileToken, setProfileToken] = useState('');
  const [ingressMode, setIngressMode] = useState<'NONE' | 'URL_INPUT' | 'WHIP_INPUT'>('NONE');
  const [publicRtspUrl, setPublicRtspUrl] = useState('');

  const reset = () => {
    setStep('probe'); setProbing(false); setSaving(false); setError(null);
    setProbe(null); setName(''); setProfileToken(''); setIngressMode('NONE'); setPublicRtspUrl('');
  };

  const handleProbe = async () => {
    setError(null);
    setProbing(true);
    try {
      const res = await probeCamera(creds);
      setProbe(res);
      if (res.error) {
        setError(res.error);
      } else if ((res.profiles ?? []).length > 0) {
        setProfileToken(res.profiles[0].profile_token);
        const m = res.device_info?.model ?? 'Camera';
        setName(m);
        setStep('configure');
      } else {
        setError('No profiles found — check ONVIF service path.');
      }
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setProbing(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const ingress = ingressMode === 'NONE' ? undefined : {
        mode: ingressMode,
        public_rtsp_url: ingressMode === 'URL_INPUT' && publicRtspUrl ? publicRtspUrl : undefined,
      };
      await createCamera({ property_id: propertyId, name, profile_token: profileToken, onvif: creds, ingress });
      onCreated();
      onClose();
      reset();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { onClose(); reset(); }} title="Add camera" size="md">
      {error && (
        <div className="mb-3 px-3 py-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
      )}

      {step === 'probe' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Works with any ONVIF-compliant camera or NVR (Hikvision, Dahua, Axis, Turing, Bosch, Hanwha, etc.).
          </p>
          <Input label="Hostname or IP"   value={creds.host}     onChange={(e) => setCreds({ ...creds, host: e.target.value })}     placeholder="192.168.1.50" />
          <Input label="ONVIF Port"       value={creds.port ?? ''} onChange={(e) => setCreds({ ...creds, port: e.target.value })}    placeholder="80" />
          <Input label="Username"         value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} />
          <Input label="Password"         type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleProbe} disabled={probing || !creds.host || !creds.username || !creds.password}>
              {probing ? 'Probing…' : 'Probe'}
            </Button>
          </div>
        </div>
      )}

      {step === 'configure' && probe && (
        <div className="space-y-4">
          <div className="text-xs text-gray-500">
            <span className="font-medium text-gray-700">{probe.device_info?.manufacturer}</span> {probe.device_info?.model}
            {probe.device_info?.firmware_version ? ` · fw ${probe.device_info.firmware_version}` : ''}
          </div>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Front Door / Lobby / etc." />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Channel / profile</label>
            <select value={profileToken} onChange={(e) => setProfileToken(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
              {(probe.profiles ?? []).map((p: OnvifProfile) => (
                <option key={p.profile_token} value={p.profile_token}>
                  {p.name ?? p.profile_token}{p.resolution ? ` — ${p.resolution}` : ''}{p.encoding ? ` (${p.encoding})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Streaming mode</label>
            <select value={ingressMode} onChange={(e) => setIngressMode(e.target.value as any)} className="w-full border rounded px-3 py-2 text-sm">
              <option value="NONE">No remote streaming (LAN only)</option>
              <option value="URL_INPUT">RTSP pull (NVR must be publicly reachable)</option>
              <option value="WHIP_INPUT">WHIP push from on-site agent</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              WHIP push is recommended — an on-site agent (Pi camera bridge or go2rtc) reads RTSP locally and pushes to LiveKit. Avoids exposing the NVR.
            </p>
          </div>
          {ingressMode === 'URL_INPUT' && (
            <Input
              label="Public RTSP URL (optional)"
              value={publicRtspUrl}
              onChange={(e) => setPublicRtspUrl(e.target.value)}
              placeholder="rtsp://public.example.com:554/stream1 — or leave blank to use the ONVIF-discovered URL"
            />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setStep('probe')}>Back</Button>
            <Button onClick={handleSave} disabled={saving || !name || !profileToken}>
              {saving ? 'Adding…' : 'Add camera'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
